/**
 * Module 'trust': node identity, the connection challenge, signed command frames, node-side
 * ceilings, the tamper-evident audit chain and kill switches (architecture doc 11.3 to 11.5,
 * 11.9, 11.10).
 *
 *   hub -> agent  ext trust 'challenge' {nonce, hubKey, ...}         first message on every socket, once
 *   agent -> hub  hello + `trust: NodeProof`                          the node signs the hub nonce, the hub
 *                                                                     key fingerprint and its connection counter
 *   hub -> agent  welcome, error, act, ext, logs.subscribe/unsubscribe carry `cmd: CommandFrame`,
 *                 signed with the hub command key, bound to the node and this socket's challenge
 *   agent -> hub  ext trust 'frame.rejected' / 'ceilings'             reports
 *
 * The handshake rules both ends follow are in docs/architecture/spec.md ("Node trust"); the
 * shared test vectors are in ./__fixtures__/node-trust-v2.json (scripts/node-trust-vectors.mts).
 *
 * Everything here is browser-safe: signing and verifying happen in the hub and the agent with
 * node:crypto; this file only fixes the shapes, the exact bytes that get signed and the checks
 * that do not need a signature.
 */
import type { ActionKind, AuditEntry, Id, Ts } from '../index.ts';
import { TERM_CEILING_CAPABILITIES } from './term.ts';
import { normalizeSessionStartParams, sessionModeOf } from './containment.ts';
import { SESSION_PERMISSION_MODES } from './scheduler.ts';
import type { SessionPermissionMode } from './containment.ts';

export const TRUST_EXT = 'trust';
/** A command frame is valid for this long after it was issued. */
export const COMMAND_TTL_MS = 30_000;
/** Frames may arrive slightly after their hub-side expiry over a slow link. */
export const COMMAND_CLOCK_SLACK_MS = 2_000;
/**
 * How long an agent waits for the hub's challenge on a new socket before closing it and trying
 * again with backoff. No hello ever goes out without a challenge: there is no fallback to a
 * hello without a key proof.
 */
export const TRUST_CHALLENGE_WAIT_MS = 10_000;
/** Domain separation: a signature for one purpose is never valid for another. */
export const TRUST_HELLO_CONTEXT = 'airside-node-hello-v1';
/** v2 hello proofs also sign the hub key fingerprint and the connection counter. */
export const TRUST_HELLO_CONTEXT_V2 = 'airside-node-hello-v2';
export const TRUST_FRAME_CONTEXT = 'airside-command-frame-v1';
export const AUDIT_GENESIS = '0'.repeat(64);

/** ext trust 'challenge', hub -> agent, first message on every agent socket. */
export interface TrustChallenge {
  /** Random, single use, bound to this socket. The node signs it. */
  nonce: string;
  /** The hub's command-signing public key: base64 SPKI DER, Ed25519. */
  hubKey: string;
  hubKeyFingerprint: string;
  /** The hub's clock, so the node can check frame expiry without trusting its own clock. */
  at: Ts;
}

/** Where the node keeps its private key. 'file' is the 0600 fallback and is flagged in the UI. */
export type NodeKeyStorage = 'file' | 'keystore';

/** Carried in hello as `trust`: proof that this node holds its private key. */
export interface NodeProof {
  /** 'p256': ECDSA P-256, raw r||s signatures, from a node that paired (ADR 0042 D1, D3). */
  alg: 'ed25519' | 'p256';
  /** base64 SPKI DER (base64url is accepted too). */
  publicKey: string;
  storage: NodeKeyStorage;
  /** The hub's challenge nonce this proof answers. */
  nonce: string;
  /** base64 Ed25519 signature over proofSigningInput(proof, machineId). */
  sig: string;
  /**
   * 2: the signature also covers `hubKeyFingerprint` and `connCounter` (helloSigningInputV2).
   * Absent: a v1 proof from an older agent (helloSigningInput).
   */
  v?: 2;
  /** v2: the fingerprint of the hub key the node is talking to. The hub refuses one that is not its own (a relay). */
  hubKeyFingerprint?: string;
  /**
   * v2: this node's connection counter, raised and saved before every hello. The hub accepts a
   * connection for an identity only with a counter above the last one it saw.
   */
  connCounter?: number;
}

/** A hello that may carry a node proof (older agents send none). */
export interface TrustHelloFields {
  trust?: NodeProof;
}

/** The exact bytes a v1 node signs to answer a challenge. Kept so older agents still verify. */
export function helloSigningInput(nonce: string, machineId: string | null | undefined, publicKey: string): string {
  return `${TRUST_HELLO_CONTEXT}\n${nonce}\n${machineId ?? ''}\n${publicKey}`;
}

/** What a v2 hello proof signs. */
export interface HelloSigningFields {
  nonce: string;
  machineId: string | null | undefined;
  publicKey: string;
  hubKeyFingerprint: string;
  connCounter: number;
}

/**
 * The exact bytes a v2 node signs: the v1 lines plus the hub key fingerprint and the connection
 * counter (a decimal integer), so a proof made for one hub key is void at any other.
 */
export function helloSigningInputV2(f: HelloSigningFields): string {
  return `${TRUST_HELLO_CONTEXT_V2}\n${f.nonce}\n${f.machineId ?? ''}\n${f.publicKey}\n${f.hubKeyFingerprint}\n${Math.trunc(f.connCounter)}`;
}

/** A connection counter both ends accept: a positive safe integer. */
export function isConnCounter(v: unknown): v is number {
  return typeof v === 'number' && Number.isSafeInteger(v) && v > 0;
}

/** The bytes a proof's signature covers, by its version (null: a malformed v2 proof). */
export function proofSigningInput(proof: Pick<NodeProof, 'v' | 'nonce' | 'publicKey' | 'hubKeyFingerprint' | 'connCounter'>, machineId: string | null | undefined): string | null {
  if (proof.v === undefined) return helloSigningInput(proof.nonce, machineId, proof.publicKey);
  if (proof.v !== 2 || typeof proof.hubKeyFingerprint !== 'string' || !proof.hubKeyFingerprint || !isConnCounter(proof.connCounter)) return null;
  return helloSigningInputV2({ nonce: proof.nonce, machineId, publicKey: proof.publicKey, hubKeyFingerprint: proof.hubKeyFingerprint, connCounter: proof.connCounter });
}

/**
 * The hub -> node messages that carry a signed `cmd`. A node acts on none of them unsigned:
 * `welcome` and `error` before any other check, the rest through the frame gate. `ping` and
 * `pong` stay unsigned (they change nothing).
 */
export const SEALED_HUB_MESSAGES: readonly string[] = ['welcome', 'error', 'act', 'ext', 'logs.subscribe', 'logs.unsubscribe'];

export function isSealedHubMessage(t: unknown): boolean {
  return typeof t === 'string' && SEALED_HUB_MESSAGES.includes(t);
}

/** The signed envelope on every sealed hub -> node message (SEALED_HUB_MESSAGES), as `cmd`. */
export interface CommandFrame {
  /** The machine it is for. '' only on an error to a socket that has not named a machine yet. */
  nodeId: Id;
  /**
   * Strictly increasing for a hub key, across connections and hub restarts (the hub uses
   * max(last + 1, issuedAt * 1000) and saves a reserve above it). Nodes keep the highest they
   * accepted per hub key.
   */
  seq: number;
  /** Random, never reused. */
  nonce: string;
  issuedAt: Ts;
  /** issuedAt + COMMAND_TTL_MS. */
  expiresAt: Ts;
  /** The challenge nonce of the socket the frame was sent on: a frame from another connection is void. */
  session: string;
  /** Fingerprint of the signing key. */
  kid: string;
  /** base64 Ed25519 signature over frameSigningInput(message). */
  sig: string;
}

export type SignedFields = { cmd?: CommandFrame };

/** The exact bytes the hub signs for a command: the whole message with cmd.sig left out. */
export function frameSigningInput(msg: Record<string, unknown>): string {
  const cmd = msg.cmd && typeof msg.cmd === 'object' ? { ...(msg.cmd as Record<string, unknown>) } : undefined;
  if (cmd) delete cmd.sig;
  return `${TRUST_FRAME_CONTEXT}\n${canonicalJson({ ...msg, ...(cmd ? { cmd } : {}) })}`;
}

/** Why a node dropped a frame. */
export type FrameRejectReason =
  | 'unsigned'
  | 'bad-signature'
  | 'expired'
  | 'not-yet-valid'
  | 'replayed-seq'
  | 'replayed-nonce'
  | 'wrong-node'
  | 'wrong-session'
  | 'hub-key-changed'
  | 'no-hub-key';

export const FRAME_REJECT_LABEL: Record<FrameRejectReason, string> = {
  unsigned: 'Command was not signed',
  'bad-signature': 'Signature did not verify',
  expired: 'Command had expired',
  'not-yet-valid': 'Command was dated in the future',
  'replayed-seq': 'Sequence number was reused',
  'replayed-nonce': 'Nonce was reused',
  'wrong-node': 'Command was for another machine',
  'wrong-session': 'Command came from another connection',
  'hub-key-changed': 'Hub signing key changed',
  'no-hub-key': 'No hub key is pinned on this machine',
};

/** What a sealed message must match on the node, besides its signature and nonce. */
export interface SealExpectation {
  /** This machine's id ('' before enrolment). For a welcome: the machineId it names. */
  nodeId: string;
  /** This socket's challenge nonce. */
  session: string;
  /** The highest seq this node accepted for the hub key (0: none yet). */
  lastSeq: number;
  /** The hub's clock as the node estimates it, or null before the welcome set it (no expiry check). */
  hubNow: number | null;
}

/**
 * The checks on a sealed message that need no cryptography, in the order every implementation
 * applies them after the signature verified: node, session, expiry, sequence. The shared test
 * vectors run through this, so the TypeScript agent, the Go agent and the control plane agree.
 */
export function sealVerdict(cmd: Partial<CommandFrame>, e: SealExpectation): FrameRejectReason | null {
  if (typeof cmd.seq !== 'number' || typeof cmd.issuedAt !== 'number' || typeof cmd.expiresAt !== 'number' || typeof cmd.nonce !== 'string') return 'unsigned';
  if (cmd.nodeId !== e.nodeId) return 'wrong-node';
  if (!e.session || cmd.session !== e.session) return 'wrong-session';
  if (cmd.expiresAt - cmd.issuedAt > COMMAND_TTL_MS) return 'expired';
  if (e.hubNow !== null) {
    if (e.hubNow > cmd.expiresAt + COMMAND_CLOCK_SLACK_MS) return 'expired';
    if (cmd.issuedAt > e.hubNow + COMMAND_CLOCK_SLACK_MS + 3_000) return 'not-yet-valid';
  }
  if (!Number.isSafeInteger(cmd.seq) || cmd.seq <= e.lastSeq) return 'replayed-seq';
  return null;
}

// ---------------------------------------------------------------------------
// Revoked while offline (review F8)
// ---------------------------------------------------------------------------

/**
 * A machine removed while it was offline: the server keeps its key, so the machine's next
 * connection can prove it is that machine and be told, with an `error` code 'revoked' sealed
 * for its own id and that socket's challenge, before any welcome. Kept at most
 * REVOCATION_TOMBSTONES_KEPT (newest first), and dropped when the owner enrols that key again.
 */
export interface RevocationTombstone {
  machineId: Id;
  /** The machine's registered node key (base64 SPKI): the proof must be by this key. */
  publicKey: string;
  revokedAt: Ts;
  /** Who removed it, as the audit names them (a member login, 'owner', 'machine'). */
  by: string;
}

export const REVOCATION_TOMBSTONES_KEPT = 1000;

/** The text of a revocation told at reconnect. `by` is cleaned and capped by the caller's store. */
export function revokedWhileOfflineMessage(by: string): string {
  // 'node': the machine removed itself (node.leave, WP-20), so a copy that reconnects is told that.
  if (by === 'node') return 'This machine removed itself from Orgus when Orgus was uninstalled on it.';
  return `This machine was removed from Orgus by ${by || 'an admin'}.`;
}

/** What the agent knows on this socket before any welcome. */
export interface PreWelcomeContext {
  /** This machine's own id, from agent.json. '' (not enrolled yet) never accepts. */
  ownMachineId: string;
  /** This socket's challenge nonce. */
  challengeNonce: string;
  /** The highest seq accepted for the pinned key (0 when the caller already checked it). */
  lastSeq?: number;
}

/**
 * The one refusal before welcome that stops an agent (F8): an `error` with code 'revoked',
 * signed by the PINNED hub key (`verify` checks the signature over frameSigningInput with that
 * key), sealed for this machine's own id (never '') and for this socket's challenge nonce, with
 * a seq above the last one seen. Anything else before welcome only retries (NET-ATK-1: a
 * refusal minted for '' or for another socket that claimed this id proves nothing). Pure: the
 * Go agent and the shared vectors (node-trust-v2.json, preWelcomeRevocation) run the same rule.
 */
export function preWelcomeRevocationOk(msg: unknown, ctx: PreWelcomeContext, verify: (input: string, sig: string) => boolean): boolean {
  if (!msg || typeof msg !== 'object' || Array.isArray(msg)) return false;
  const m = msg as Record<string, unknown>;
  if (m.t !== 'error' || m.code !== 'revoked') return false;
  if (!ctx.ownMachineId || !ctx.challengeNonce) return false;
  const cmd = m.cmd as Partial<CommandFrame> | undefined;
  if (!cmd || typeof cmd !== 'object' || typeof cmd.sig !== 'string' || !cmd.sig) return false;
  if (sealVerdict(cmd, { nodeId: ctx.ownMachineId, session: ctx.challengeNonce, lastSeq: ctx.lastSeq ?? 0, hubNow: null }) !== null) return false;
  return verify(frameSigningInput(m), cmd.sig);
}

/** ext trust 'frame.rejected', agent -> hub. */
export interface FrameRejectedReport {
  reason: FrameRejectReason;
  /** The message type that was dropped ('act' or 'ext'). */
  t: string;
  kind?: string;
  seq?: number;
  at: Ts;
}

// ---------------------------------------------------------------------------
// Node-side ceilings
// ---------------------------------------------------------------------------

/**
 * What a ceiling can refuse: an action kind, a session window ('session.start:terminal'), a
 * session permission mode ('session.start:mode:<mode>'), an agent kind
 * ('session.start:agent:<kind>'), every hub command including module messages ('*'), or hub
 * messages for one feature module ('ext:<module>').
 */
export type CeilingCapability =
  | ActionKind
  | 'session.start:terminal'
  | 'session.start:headless'
  | `session.start:mode:${SessionPermissionMode}`
  | `session.start:agent:${string}`
  | '*'
  | `ext:${string}`
  | typeof CONTAINS_HOME_CEILING;

/**
 * <ORGUS_HOME>/ceilings.json on the node. Only someone on the machine can change it. While
 * the file does not exist, DEFAULT_CEILING applies; once it exists, its `deny` is the whole
 * ceiling (`orgus agent ceiling reset` removes it, so the default applies again), plus the
 * default entries added after the file's version (CEILING_ADDED_IN) until the owner allows them.
 */
export interface CeilingsFile {
  /** CEILING_FILE_VERSION when `orgus agent ceiling` wrote it; 1 (or none) before 19 Oct 2026. */
  version: number;
  deny: string[];
  updatedAt: Ts;
  note?: string;
}

/**
 * The ceiling entry for a session or command the hub asks to start in a folder that contains
 * this machine's Orgus home (containment review F2, ADR 0053 amendment of 19 Oct 2026). A session
 * there can read the home's keys without naming them (a glob, a recursive grep, Grep or Glob over
 * the folder), which neither the text check nor the real-path check can see. In the default
 * ceiling; only the owner, on that computer, lifts it (`orgus agent ceiling allow
 * cwd:contains-home`). Sessions a person starts there themselves are not affected.
 */
export const CONTAINS_HOME_CEILING = 'cwd:contains-home';

/**
 * The version `orgus agent ceiling` writes. A file of an earlier version (or none) was written
 * before the entries CEILING_ADDED_IN names joined the default ceiling, so it refuses them too
 * until the owner allows them, which writes the current version: a default entry added later
 * holds on machines that already set a ceiling (the Go agent reads it the same way).
 */
export const CEILING_FILE_VERSION = 2;

/** Default ceiling entries added after version 1, and the version that added each. */
export const CEILING_ADDED_IN: Readonly<Record<string, number>> = { [CONTAINS_HOME_CEILING]: 2 };

/** A ceiling file's deny list as it holds: its own entries plus the default entries added after its version. */
export function ceilingDenyOfFile(deny: readonly string[], version: unknown): string[] {
  const v = typeof version === 'number' && Number.isFinite(version) ? version : 1;
  const out = [...deny];
  for (const [entry, since] of Object.entries(CEILING_ADDED_IN)) if (v < since && !out.includes(entry)) out.push(entry);
  return out;
}

/**
 * What a machine refuses before anyone on it has set a ceiling (design D9): raw commands,
 * shells, terminals the hub can type into (until terminal streams are end to end, D12),
 * sessions that skip permission checks (Codex full access maps to bypassPermissions), the
 * assistant's harness, where Claude Code's own tools run on the hub's say-so (review F2,
 * ASSISTANT_HARNESS_CEILING), and the landing queue's git work, which clones, rebases and
 * pushes to origin with this computer's git credentials on the hub's word (LANDING_CEILING,
 * 18 Oct 2026; a landing verifier allows it and job.run), and sessions or commands the hub
 * starts in a folder that contains this machine's Orgus home (CONTAINS_HOME_CEILING).
 * "Agents can work in your repositories; shells and unsandboxed agents need you to allow them
 * on this computer." The Go agent has the same list (protocol.DefaultCeiling).
 */
export const DEFAULT_CEILING: readonly string[] = ['cwd:contains-home', 'ext:landing', 'job.run', 'session.start:agent:assistant', 'session.start:mode:bypassPermissions', 'term.attach', 'term.shell'];

/** The ceiling entry for the landing queue's git work on this machine (module 'landing'). */
export const LANDING_CEILING = 'ext:landing';

import type { LandingGrantReport } from './landing-consent.ts';

/**
 * A module request the machine's ceiling refused (18 Oct 2026): instead of dropping it, the agent
 * answers a hub message that carries an id with ext `<module>`, type EXT_REFUSED_TYPE, the same
 * id and an ExtRefusal, so the hub says what failed, why and what to do next instead of waiting.
 * A dropped frame (bad signature, replay) is still never answered.
 */
export const EXT_REFUSED_TYPE = 'refused';

export interface ExtRefusal {
  what: string;
  why: string;
  next: string;
  code: 'ceiling';
  /** The ceiling entry that refused it: 'ext:<module>', '*', or 'ceilings.json' (a file that cannot be used). */
  entry: string;
}

/**
 * Why this machine's ceiling refuses hub messages for `ext`, as the agent answers it, or null.
 * Both agents build these words the same way (the Go agent: protocol.ExtCeilingRefusal).
 */
export function extCeilingRefusal(ext: string, c: { deny: readonly string[]; isDefault: boolean; unreadable?: boolean }): ExtRefusal | null {
  const whose = c.isDefault ? 'default ceiling' : 'own ceiling';
  if (c.unreadable) {
    return { what: `This machine refuses ${ext} messages.`, why: 'Its ceiling file (ceilings.json) cannot be used, so it refuses every command from the hub.', next: 'On that computer, fix the file or run orgus agent ceiling reset.', code: 'ceiling', entry: 'ceilings.json' };
  }
  if (!ceilingRefusesExtModule(c.deny, ext)) return null;
  const entry = c.deny.includes('*') ? '*' : `ext:${ext}`;
  if (ext === 'landing' && entry !== '*') {
    return {
      what: 'This machine does not take landing work.',
      why: `Its ${whose} refuses ${LANDING_CEILING}: the landing queue clones, rebases and pushes to origin with that computer's git credentials on the hub's word.`,
      next: `On that computer run orgus agent ceiling allow ${LANDING_CEILING}, and orgus agent ceiling allow job.run for the verify command; or let the landing queue use another machine.`,
      code: 'ceiling',
      entry,
    };
  }
  return {
    what: `This machine refuses ${ext} messages.`,
    why: `Its ${whose} refuses ${entry === '*' ? 'every command from the hub' : entry}.`,
    next: entry === '*' ? 'Only someone on that computer can change it (orgus agent ceiling).' : `Only someone on that computer can change it: orgus agent ceiling allow ${entry}.`,
    code: 'ceiling',
    entry,
  };
}

/**
 * The ceiling entry for the assistant's harness (Claude Code's own tools, each use asked of
 * the hub): the harness counts as a headless session of agent kind 'assistant', so 'session.start',
 * 'session.start:headless' and '*' refuse it too.
 */
export const ASSISTANT_HARNESS_CEILING = 'session.start:agent:assistant';

/** The module switch for the assistant on a machine: refuses every assistant reply there. */
export const ASSISTANT_CEILING = 'ext:assistant';

/**
 * The ceiling entry that refuses an assistant reply on this machine, or null (18 Oct 2026, ADR
 * 0053 amendment). Every reply starts Claude Code here, so with or without the harness it is
 * also a headless session start: '*', 'ext:assistant', 'session.start', 'session.start:headless'
 * and the mode entry refuse it. A reply carries no permission mode (Claude Code runs in its
 * default mode, bypass switched off by the run's settings file), so the mode entry is
 * 'session.start:mode:default' (sessionModeOf). Only the harness also needs
 * 'session.start:agent:assistant' (ASSISTANT_HARNESS_CEILING), which the default ceiling refuses,
 * so a machine with the default ceiling runs plain replies and refuses the harness. The Go agent
 * has the same rule (protocol.AssistantRunCeilingHit).
 */
export function assistantRunCeilingHit(deny: readonly string[], harness: boolean): string | null {
  if (deny.includes('*')) return '*';
  if (deny.includes(ASSISTANT_CEILING)) return ASSISTANT_CEILING;
  const hit = ceilingHit(deny, 'session.start', { mode: 'headless', agentKind: 'assistant' });
  if (hit === ASSISTANT_HARNESS_CEILING && !harness) return null;
  return hit;
}

/**
 * What failed, why and what to do next when the ceiling refuses an assistant reply (`entry`, from
 * assistantRunCeilingHit). `machine` names it from the hub's side; the agent says "This machine".
 * Both agents and the hub build these words the same way.
 */
export function assistantCeilingRefusal(entry: string, o: { harness: boolean; isDefault: boolean; machine?: string }): { what: string; why: string; next: string } {
  const own = o.machine ? `${o.machine}'s` : "This machine's";
  const whose = `${own} ${o.isDefault ? 'default ceiling' : 'own ceiling'}`;
  const harnessOnly = entry === ASSISTANT_HARNESS_CEILING;
  const where = o.machine ? `on ${o.machine}` : 'on this machine';
  return {
    what: harnessOnly ? `The assistant did not start: Claude Code's own tools are refused ${where}.` : `The assistant did not start: ${o.machine ?? 'this machine'} refuses assistant replies.`,
    why: harnessOnly
      ? `${whose} refuses ${entry}, and the harness (Claude Code's own tools on the hub's say-so) counts as a session of agent kind assistant.`
      : `${whose} refuses ${entry === '*' ? 'every command from the hub (*)' : entry}, and every assistant reply starts Claude Code ${where}, so it counts as a session start.`,
    next: `The machine owner can allow it with orgus agent ceiling allow ${entry === '*' ? "'*'" : entry}${harnessOnly ? ', or turn the harness off in Settings, Assistant' : ''}.`,
  };
}

/**
 * How far a ceiling holds. 'advisory' until approvals are checked on the node (D10): the agent
 * refuses commands from the hub that the ceiling names, but the file is in the user's own
 * folder, so any program running as that user (a session or job started earlier included)
 * can change it.
 */
export type CeilingEnforcement = 'advisory';
export const CEILING_ENFORCEMENT: CeilingEnforcement = 'advisory';
/** One sentence on what a ceiling does and does not stop. */
export const CEILING_ADVISORY_NOTE =
  'Enforced by the machine against commands from the hub. Advisory against programs running as you on that computer, which can change the ceiling file.';

/** Every name a ceiling understands, besides 'ext:<module>' and 'session.start:agent:<kind>'. */
export const CEILING_CAPABILITIES: readonly string[] = [
  '*',
  'session.start',
  'session.start:terminal',
  'session.start:headless',
  ...SESSION_PERMISSION_MODES.map((m) => `session.start:mode:${m}`),
  'session.stop',
  'job.run',
  'job.cancel',
  'worktree.remove',
  'worktree.integrate',
  'worktree.landed',
  'pipeline.pause',
  'pipeline.resume',
  'pipeline.restart',
  'project.add',
  'project.remove',
  ...TERM_CEILING_CAPABILITIES,
  CONTAINS_HOME_CEILING,
];

export function isCeilingCapability(v: string): boolean {
  return CEILING_CAPABILITIES.includes(v) || /^ext:[a-z0-9][a-z0-9_-]{0,40}$/.test(v) || /^session\.start:agent:[a-z0-9][a-z0-9_-]{0,40}$/.test(v);
}

/** The ceiling entry that refuses this action, or null when the ceiling allows it. */
export function ceilingHit(deny: readonly string[], kind: ActionKind, raw: Record<string, unknown>): string | null {
  if (deny.includes('*')) return '*';
  if (deny.includes(kind)) return kind;
  if (kind === 'session.start') {
    // F3: the values the launch will use ("terminal " is a terminal, "Codex" is codex).
    const params = normalizeSessionStartParams(raw);
    const mode = params.mode === 'terminal' ? 'terminal' : 'headless';
    if (deny.includes(`session.start:${mode}`)) return `session.start:${mode}`;
    // A mode that is not one of the four counts as bypassPermissions (sessionModeOf).
    const pm = `session.start:mode:${sessionModeOf(params)}`;
    if (deny.includes(pm)) return pm;
    const agent = `session.start:agent:${typeof params.agentKind === 'string' && params.agentKind ? params.agentKind : 'claude-code'}`;
    if (deny.includes(agent)) return agent;
  }
  return null;
}

/** Does the ceiling refuse hub messages for this module? 'ext:<module>', or '*', which covers ext frames too. */
export function ceilingRefusesExtModule(deny: readonly string[], ext: string): boolean {
  return deny.includes('*') || deny.includes(`ext:${ext}`);
}

/** ext trust 'ceilings', agent -> hub, on connect and when the file changes. */
export interface CeilingsReport {
  /** What the machine refuses now (the default ceiling while it has no file). */
  deny: string[];
  updatedAt: Ts | null;
  /** No ceiling file: DEFAULT_CEILING applies. Absent from older agents. */
  default?: boolean;
  /** How far the ceiling holds. Absent from older agents (read it as advisory too). */
  enforcement?: CeilingEnforcement;
  /**
   * Checkouts this machine's person allowed landing work for (landing-consent.json, 1 Nov 2026):
   * the ceiling's ext:landing and job.run then hold for everything but that work. Absent when none.
   */
  landing?: LandingGrantReport[];
}

// ---------------------------------------------------------------------------
// Hub state and API
// ---------------------------------------------------------------------------

export type NodeTrustStatus = 'key' | 'legacy';

/**
 * A key a machine enrolled before keys has proved, waiting for an admin. It is never bound on
 * its own: an admin compares the fingerprint with the one the machine shows and approves it.
 */
export interface PendingNodeKey {
  fingerprint: string;
  storage: NodeKeyStorage;
  firstSeenAt: Ts;
  lastSeenAt: Ts;
  /** The address it connected from. */
  remote: string;
  /** How many times a different key was proved for the same machine since (a sign of a copy). */
  conflicts: number;
  /**
   * Other keys proved for this machine while this one waited (newest first, at most
   * PENDING_KEYS_KEPT). An admin may approve any of them: the one the machine itself shows.
   * Optional for older hubs.
   */
  others?: PendingNodeKeyAlt[];
  /**
   * The waiting key changed too often (RN-3): it no longer follows new keys, which are only
   * listed in `others`. A sign that copies of this machine are running. Optional for older hubs.
   */
  frozen?: boolean;
}

export interface PendingNodeKeyAlt {
  fingerprint: string;
  storage: NodeKeyStorage;
  lastSeenAt: Ts;
  remote: string;
}

/** How many other proved keys a waiting machine key keeps for the admin to choose from. */
export const PENDING_KEYS_KEPT = 5;

/**
 * An identity seen in two places at once (design D4): a v2 connection counter at or below the
 * last one, a jump far above it, or a second live connection. Write actions and terminal input
 * to the machine are refused until an admin releases it (POST /api/trust/nodes/:id/release) or
 * removes and re-enrols it.
 */
export interface NodeQuarantine {
  at: Ts;
  reason: string;
  /** The addresses involved, the existing connection's first. */
  remotes: string[];
}

/** A v2 connection counter may rise by at most this much in one step (a bigger jump is a fork). */
export const CONN_COUNTER_MAX_STEP = 1_000;

export interface NodeTrustInfo {
  machineId: Id;
  /** 'key': proved its Ed25519 key on connect. 'legacy': enrolled before keys, secret only. */
  status: NodeTrustStatus;
  fingerprint: string | null;
  storage: NodeKeyStorage | null;
  registeredAt: Ts | null;
  lastVerifiedAt: Ts | null;
  /** What the node's own ceiling refuses, as last reported. null: never reported. */
  ceilings: string[] | null;
  /** The machine has no ceiling file, so DEFAULT_CEILING applies. Optional for older hubs and agents. */
  ceilingDefault?: boolean;
  /** How far the ceiling holds (CEILING_ADVISORY_NOTE). Optional for older hubs. */
  ceilingEnforcement?: CeilingEnforcement;
  /** Below the hub's minimum agent version: write commands are refused. */
  belowMinimum: boolean;
  /** A legacy machine's first key, waiting for an admin (POST /api/trust/nodes/:id/key). Optional for older hubs. */
  pendingKey?: PendingNodeKey | null;
  /** The hello proof version of its last connection: 'v1' agents should be updated. */
  proof?: 'v1' | 'v2' | null;
  /** Seen in two places at once: writes refused until an admin acts. Optional for older hubs. */
  quarantine?: NodeQuarantine | null;
}

/**
 * Whether the hub accepts agent connections from other computers. Until the encrypted node
 * channel ships, agent traffic over a network is plain text, so new hubs accept only agents on
 * their own computer and the owner turns the rest on knowingly.
 */
export interface RemoteMachinesState {
  allowed: boolean;
  /** While allowed: the warning a banner shows. null while only this computer is accepted. */
  warning: string | null;
  /** Allowed because this hub accepted other computers before this rule existed, not by an owner's choice. */
  legacy: boolean;
}

/**
 * The relay (ORGU-284): an outbound link from the hub to the relay service, so the owner can reach
 * the console from anywhere. Off by default; only the owner turns it on, and turning it off closes
 * the link at once, which ends every remote browser's access. Nothing listens beyond loopback for
 * it: the hub dials out.
 *
 *   off           the owner has not turned it on (or turned it off)
 *   unconfigured  on, but this hub has no relay address (ORGUS_RELAY_URL)
 *   connecting    on, dialling (or redialling after a drop, with backoff)
 *   connected     on, the link is up
 */
export type RelayLinkStatus = 'off' | 'unconfigured' | 'connecting' | 'connected';

export interface RelayState {
  on: boolean;
  link: RelayLinkStatus;
  /** When the link came up (connected only). */
  since: Ts | null;
  /** The last failure while on: what failed, in a sentence. */
  error: string | null;
}

/** How long turning the relay off may take to close the link (AC: within 5 seconds). */
export const RELAY_CLOSE_MS = 5_000;

/** The banner text while other computers may connect. No em dashes (UI string). */
export const REMOTE_MACHINES_WARNING =
  'Machines on other computers can connect to this hub. Their traffic is not encrypted yet, so anyone on the network can read it and could send them commands. Use this only on a private network such as Tailscale or behind a TLS proxy, or turn it off in Settings > Trust.';

/** Why an agent connection from another computer was refused. Errors say what, why and what next. */
export const REMOTE_MACHINES_REFUSED =
  'This hub accepts machines only from its own computer, because agent traffic over a network is not encrypted yet. Ask the hub owner to turn on "Machines on other computers" in Settings > Trust (on a private network such as Tailscale), then start the agent again.';

/** Extra fields on POST /api/join-tokens about the hub key the command pins. */
export interface JoinTokenTrustFields {
  /**
   * The hub command-key fingerprint the join command pins (`--hub-key`). Present only when the
   * console was opened on this computer (loopback) or over HTTPS, so a network attacker cannot
   * swap it on the way to the person copying the command.
   */
  hubKeyFingerprint?: string;
  /** When the fingerprint was left out: why, and what to do instead. */
  hubKeyNote?: string;
}

/** Why the join command carries no hub key fingerprint over plain HTTP. */
export const HUB_KEY_WITHHELD_NOTE =
  'This page reached the hub over plain HTTP from another computer, so the command does not pin the hub key: someone on the network could have changed it. Open Orgus on the hub computer (or over HTTPS) to add a machine.';

/**
 * The join command POST /api/join-tokens hands out: the Go node agent (agents/node-agent, R-ND-5,
 * R-ND-6), the token on standard input (SD-4) and the hub key pinned when it may be (NET-3). A
 * join token is for developers and automation; people add a computer with Machines > Add machine
 * (download, run, approve), which needs no token.
 */
export function joinCommands(token: string, hubUrl: string, hubKeyFingerprint: string | null): { windows: string; posix: string } {
  const args = `--hub ${hubUrl} --token-stdin${hubKeyFingerprint ? ` --hub-key ${hubKeyFingerprint}` : ''}`;
  return { windows: `'${token}' | .\\orgus-node-agent.exe ${args}`, posix: `printf '%s\\n' '${token}' | ./orgus-node-agent ${args}` };
}

/** Where and how to run the join command (runIn on POST /api/join-tokens). */
export function joinRunIn(ttlMs: number): string {
  return (
    'For developers and automation; to add a computer, use Machines > Add machine (download, run, approve). ' +
    `Run it in the folder that holds orgus-node-agent (from Machines > Add machine, or built from agents/node-agent). The token works once and expires in ${Math.round(ttlMs / 60_000)} minutes; it goes in on standard input, so it never shows in a process list.`
  );
}

export interface KillSwitch {
  by: string;
  at: Ts;
  reason: string;
}

export interface PausedProject extends KillSwitch {
  projectId: Id;
}

export interface TrustRejection {
  /** The latest refusal in this row. */
  at: Ts;
  machineId: Id | null;
  what: string;
  /** The latest refusal's reason. */
  reason: string;
  /** How many refusals this row stands for (absent: one). */
  count?: number;
  /** The first of them (absent: `at`). */
  firstAt?: Ts;
}

/**
 * SD-3: before a newer connection is judged against an older one that was heard from recently,
 * the hub (or control plane) pings the older one and waits this long for any answer. Only an
 * older connection that answers now makes the newer one a fork; one that does not is replaced.
 */
export const LINK_PROBE_MS = 3_000;

/** The same refusal of the same machine within this long of a row's first one folds into that row. */
export const REJECTION_WINDOW_MS = 10 * 60_000;

/**
 * Repeated identical refusals (a machine retrying every minute; identical but for numbers such as
 * its connection counter) become one row per machine and refusal per window, with a count, not
 * one row and one audit entry per attempt. `list` is newest first and is changed in place (the
 * row moves to the front). `folded`: the refusal
 * joined an open row, whose `at` before this one was `prevAt`; otherwise a new row was added and
 * `closed` is the same machine's previous row of that kind, if any (its window is over).
 */
export function foldRejection(
  list: TrustRejection[],
  r: TrustRejection,
  keep: number,
  windowMs: number = REJECTION_WINDOW_MS,
): { row: TrustRejection; folded: true; prevAt: Ts } | { row: TrustRejection; folded: false; closed: TrustRejection | null } {
  const shape = (s: string): string => s.replace(/\d+/g, '#');
  const i = list.findIndex((x) => x.machineId === r.machineId && x.what === r.what && shape(x.reason) === shape(r.reason));
  const open = i >= 0 ? list[i]! : null;
  if (open && r.at - (open.firstAt ?? open.at) < windowMs && r.at >= open.at) {
    const prevAt = open.at;
    const row: TrustRejection = { ...open, at: r.at, reason: r.reason, count: (open.count ?? 1) + 1, firstAt: open.firstAt ?? open.at };
    list.splice(i, 1);
    list.unshift(row);
    return { row, folded: true, prevAt };
  }
  const row: TrustRejection = { ...r, count: 1, firstAt: r.at };
  list.unshift(row);
  if (list.length > keep) list.length = keep;
  return { row, folded: false, closed: open };
}

/** The audit text for a new window's first refusal: says how many the closed window held. */
export function rejectionAuditText(what: string, reason: string, closed: TrustRejection | null): string {
  const n = closed?.count ?? 1;
  return n > 1 ? `${what}: ${reason} (${n} refusals of this kind in the window before this one.)` : `${what}: ${reason}`;
}

/** state.ext.trust and GET /api/trust */
export interface TrustState {
  /** Org-wide freeze: every write action is refused, reads still work. */
  frozen: KillSwitch | null;
  pausedProjects: PausedProject[];
  minAgentVersion: string | null;
  /** Refuse write actions to nodes that have not proved a key yet. On by default for new hubs. */
  requireKeys: boolean;
  /** Agent connections from other computers. Optional for older hubs. */
  remoteMachines?: RemoteMachinesState;
  /** The relay to app.orgus.ai (ORGU-284). Optional for older hubs. */
  relay?: RelayState;
  /**
   * The key that signs every command to a machine. `error` is set when it could not be loaded
   * (for example a malformed COMMAND_SIGNING_KEY secret): what failed and how to fix it. Write
   * actions are refused until it loads; reads keep working.
   */
  commandKey: {
    fingerprint: string;
    createdAt: Ts;
    error?: string;
    /** R-SC-8: where the hub keeps it. Unset on the control plane (a Worker secret). */
    storage?: 'keystore' | 'file';
    /** "Windows Credential Manager", when storage is keystore. */
    keystore?: string;
    /** Set when it is in a file because no OS keystore could be used: what to do about it. */
    warning?: string;
  };
  nodes: NodeTrustInfo[];
  /** Newest first, the last 50 refused handshakes and dropped frames. */
  rejections: TrustRejection[];
  audit: { head: string | null; chained: number };
}

/** PUT /api/trust/settings */
export interface TrustSettingsPatch {
  minAgentVersion?: string | null;
  requireKeys?: boolean;
  /** Owner only. */
  remoteMachines?: boolean;
  /** Owner only. Audited 'trust.relay' with 'relay on' or 'relay off'. */
  relay?: boolean;
}

/** POST /api/trust/nodes/:id/key: bind a legacy machine's pending key. The fingerprint must match the pending one. */
export interface ApproveNodeKeyBody {
  fingerprint: string;
}

/** An audit entry once the hub chains it. Readers that do not know these fields ignore them. */
export type ChainedAuditEntry = AuditEntry & {
  prevHash?: string;
  hash?: string;
  seq?: number;
};

/** GET /api/trust/audit/verify and `orgus audit verify`. */
export interface AuditVerifyResult {
  ok: boolean;
  entries: number;
  /** Entries written before hash chaining existed (not covered by the chain). */
  legacy: number;
  chained: number;
  head: string | null;
  /** The anchor the hub keeps beside the log (catches a cut tail). */
  anchor: { head: string; count: number; matches: boolean } | null;
  brokenAt?: { line: number; id: string | null; reason: string };
  /**
   * How many chained entries record each machine lifecycle event (MACHINE_LIFECYCLE_ACTIONS), so
   * a verify shows that minting, enrolment, pairing and revocation are in the chain (WP-16).
   */
  machineEvents?: Record<string, number>;
}

/** The machine lifecycle events every server audits, and verify counts (R-SC-2). */
export const MACHINE_LIFECYCLE_ACTIONS: readonly string[] = ['join-token.mint', 'join-token.cancel', 'machine.enrol', 'machine.revoke', 'machine.self-revoke', 'pairing.requested', 'pairing.approved', 'pairing.denied'];

/** Labels for the audit entries this module writes (their action names start with "trust."). */
export const TRUST_AUDIT_LABEL: Record<string, string> = {
  'trust.freeze': 'Freeze all writes',
  'trust.unfreeze': 'Unfreeze',
  'trust.project-pause': 'Pause project',
  'trust.project-resume': 'Resume project',
  'trust.settings': 'Change trust settings',
  'trust.node-key-registered': 'Register machine key',
  'trust.node-key-upgraded': 'Upgrade machine to a key',
  'trust.node-key-pending': 'Machine key waiting for approval',
  'trust.node-key-approved': 'Approve machine key',
  'trust.node-key-dismissed': 'Dismiss machine key',
  'trust.identity-conflict': 'Possible copy of a machine identity',
  'trust.remote-machines': 'Machines on other computers',
  'trust.relay': 'Remote access relay',
  'trust.ceiling-changed': 'Machine ceiling changed',
  'trust.rejected': 'Refused by trust',
  'trust.command-key-export': 'Export hub keys',
  'trust.command-key-import': 'Import hub keys',
};

/** The machineId on audit entries that concern the whole hub rather than one machine. */
export const HUB_AUDIT_MACHINE = 'hub';

/** Actions that stop or contain work: allowed even while frozen or paused. */
export const DE_ESCALATION_ACTIONS: readonly ActionKind[] = ['session.stop', 'job.cancel', 'pipeline.pause'];

// ---------------------------------------------------------------------------
// Where a key may be learned
// ---------------------------------------------------------------------------

/**
 * True for a loopback host name or socket address: localhost, 127.0.0.0/8, ::1 and their
 * IPv4-mapped forms, with or without IPv6 brackets.
 */
export function isLoopbackAddress(host: string | null | undefined): boolean {
  if (!host) return false;
  let h = host.trim().toLowerCase();
  if (h.startsWith('[') && h.endsWith(']')) h = h.slice(1, -1);
  if (h.startsWith('::ffff:')) h = h.slice(7);
  if (h === 'localhost' || h.endsWith('.localhost') || h === '::1' || h === '0:0:0:0:0:0:0:1') return true;
  return /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h);
}

/**
 * Whether a node with no pinned hub key, and no fingerprint from its join command, may pin the
 * key the hub's challenge carries: only over loopback (nothing on the network in between) or TLS
 * (wss/https: the certificate authenticates the hub). Over plain ws/http to another computer the
 * fingerprint must come from the join command (`--hub-key`), never from the wire.
 */
export function firstUsePinAllowed(hubUrl: string): boolean {
  if (typeof hubUrl !== 'string') return false;
  const raw = hubUrl.trim();
  // NET-ATK-3: a URL two parsers could read two ways is never trusted for a first-use pin. WHATWG
  // URL (what the agent dials with) folds `\` into `/` for http(s) and ws(s), so
  // `http://evil.example\@127.0.0.1` dials evil.example while a naive split sees 127.0.0.1.
  if (!raw || /[\\\u0000- \u007f-\u009f]/.test(raw)) return false;
  const m = /^([a-z][a-z0-9+.-]*):\/\/([^/?#]*)/i.exec(raw);
  if (!m) return false;
  const scheme = m[1]!.toLowerCase();
  if (scheme === 'https' || scheme === 'wss') return true;
  if (scheme !== 'http' && scheme !== 'ws') return false;
  const authority = m[2]!;
  // A user-info part has no business in a hub URL; refuse rather than guess which host it means.
  if (authority.includes('@') || authority.includes('%')) return false;
  const host = /^(\[[^\]]*\]|[^:]*)(?::\d*)?$/.exec(authority)?.[1];
  if (host === undefined || !isLoopbackAddress(host)) return false;
  // Classify from the parser the dialer uses, where there is one (Node and browsers both have URL).
  const Url = (globalThis as unknown as { URL?: new (s: string) => { hostname: string; username: string; password: string } }).URL;
  if (!Url) return true;
  try {
    const u = new Url(raw);
    return !u.username && !u.password && isLoopbackAddress(u.hostname);
  } catch {
    return false;
  }
}

/** A hub key fingerprint as `--hub-key` takes it: SHA256:<base64 without padding> of the SPKI DER. */
export function isKeyFingerprint(v: unknown): v is string {
  return typeof v === 'string' && /^SHA256:[A-Za-z0-9+/]{43}$/.test(v);
}

// ---------------------------------------------------------------------------
// Helpers both ends need, byte for byte
// ---------------------------------------------------------------------------

/** JSON with object keys sorted at every level and undefined dropped: the same value always gives the same text. */
export function canonicalJson(v: unknown): string {
  if (v === null || typeof v !== 'object') return v === undefined ? 'null' : JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map((x) => (x === undefined || typeof x === 'function' ? 'null' : canonicalJson(x))).join(',')}]`;
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o)
    .filter((k) => o[k] !== undefined && typeof o[k] !== 'function')
    .sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(o[k])}`).join(',')}}`;
}

/** Compare dotted versions numerically ('0.10.0' > '0.9.1'). Anything unparseable counts as 0. */
export function compareVersions(a: string, b: string): number {
  const parts = (s: string) => s.replace(/^v/i, '').split(/[.+-]/).slice(0, 3).map((p) => (/^\d+$/.test(p) ? Number(p) : 0));
  const x = parts(a);
  const y = parts(b);
  for (let i = 0; i < 3; i++) {
    const d = (x[i] ?? 0) - (y[i] ?? 0);
    if (d) return d < 0 ? -1 : 1;
  }
  return 0;
}

/** True when `v` is a version this module can compare ('1', '0.2', '0.2.1', 'v1.2.3'). */
export function isVersion(v: string): boolean {
  return /^v?\d+(\.\d+){0,2}$/.test(v.trim());
}
